// skipcq: JS-W1028
import { useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { buildHeatmapWeightedLocations } from '../../utils/firstTouchReport';
import FirstTouchHeatmapOverlay from './FirstTouchHeatmapOverlay';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '420px' };

function FirstTouchHotspotsMap({ rows }) {
  const [map, setMap] = useState(null);
  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'engage-first-touch-heatmap',
    googleMapsApiKey: typeof mapsApiKey === 'string' ? mapsApiKey : '',
  });

  const weightedLocations = useMemo(() => buildHeatmapWeightedLocations(rows), [rows]);

  if (!mapsApiKey) {
    return (
      <Alert severity="warning">
        Set <code>VITE_GOOGLE_MAPS_API_KEY</code> to render the first-touch heat map.
      </Alert>
    );
  }

  if (loadError) {
    return <Alert severity="error">Failed to load Google Maps for the heat map.</Alert>;
  }

  if (!isLoaded) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading map…
        </Typography>
      </Box>
    );
  }

  if (weightedLocations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No hotspots with coordinates yet.
      </Typography>
    );
  }

  return (
    <Box>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={DEFAULT_CENTER}
        zoom={4}
        onLoad={setMap}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {map ? (
          <FirstTouchHeatmapOverlay map={map} weightedLocations={weightedLocations} />
        ) : null}
      </GoogleMap>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Heat intensity reflects user count at each brewery or event location.
      </Typography>
    </Box>
  );
}

FirstTouchHotspotsMap.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default FirstTouchHotspotsMap;
